import { Proposal } from "creso-core/types";
import { useProposalBacktest } from "creso-core/hooks";
import {
  convertToCurrency,
  formatAsCrore,
  formatAsPercentage,
  formatProposalStepUp,
} from "creso-core/utils";
import { useId, useMemo } from "react";
import { Area, ComposedChart, Line, XAxis, YAxis } from "recharts";
import type { TooltipProps } from "recharts";
import {
  addMonths,
  differenceInDays,
  differenceInMonths,
  format,
  isValid,
} from "date-fns";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "web-design-system";
import { BENCHMARK_OPTIONS } from "@/modules/funds/components/baskets/fund-basket-backtest-chart";
import { formatDuration } from "@/modules/planning/config";
import { getBacktestStepUpParams } from "@planning/providers/proposal-provider";
import { ArrowUpWideNarrowIcon } from "lucide-react";

const DEFAULT_PERIOD = "5Y";
const DEFAULT_BENCHMARK = "NIFTY 500";

const PORTFOLIO_STROKE = "var(--chart-3)";
const BENCHMARK_STROKE = "var(--muted-foreground)";

const chartConfig = {
  portfolio: { label: "Portfolio average", color: PORTFOLIO_STROKE },
  benchmark: { label: "Benchmark", color: BENCHMARK_STROKE },
} satisfies ChartConfig;

type SeriesPoint = {
  date: string;
  portfolio_value: number;
  total_invested: number;
};

type ChartPoint = {
  date: string;
  portfolio?: number;
  benchmark?: number;
  portfolio_invested?: number;
  benchmark_invested?: number;
};

function mergeChartSeries(
  portfolioSeries: SeriesPoint[],
  benchmarkSeries: SeriesPoint[]
): ChartPoint[] {
  const map = new Map<string, Omit<ChartPoint, "date">>();
  portfolioSeries.forEach((p) => {
    const cur = map.get(p.date) ?? {};
    cur.portfolio = p.portfolio_value;
    cur.portfolio_invested = p.total_invested;
    map.set(p.date, cur);
  });
  benchmarkSeries.forEach((p) => {
    const cur = map.get(p.date) ?? {};
    cur.benchmark = p.portfolio_value;
    cur.benchmark_invested = p.total_invested;
    map.set(p.date, cur);
  });
  return [...map.entries()]
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .map(([date, v]) => ({ date, ...v }));
}

export function BacktestSection({
  proposal,
  period = DEFAULT_PERIOD,
  benchmark = DEFAULT_BENCHMARK,
  embed = false,
}: {
  proposal: Proposal;
  period?: string;
  benchmark?: string;
  embed?: boolean;
}) {
  const uid = useId().replace(/:/g, "");

  const hasLumpsum = Number(proposal.total_lumpsum_amount) > 0;
  const hasSip = Number(proposal.total_sip_amount) > 0;

  const { data, isLoading } = useProposalBacktest({
    id: proposal.id,
    period,
    sipFrequency: "MONTHLY",
    benchmark,
    ...getBacktestStepUpParams(
      proposal,
      hasSip
        ? proposal.proposal_funds.filter((f) => f.type === "SIP").length
        : 0
    ),
  });

  const chartPoints = useMemo(() => {
    if (!data) return [];
    return mergeChartSeries(data.proposal_series, data.benchmark_series);
  }, [data]);

  if (isLoading || !data || chartPoints.length === 0) return null;

  const benchmarkLabel =
    BENCHMARK_OPTIONS.find((o) => o.value === benchmark)?.label ?? benchmark;

  const formatXAxisTick = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "MMM yy");
    } catch {
      return "";
    }
  };

  return (
    <>
      <div className="mb-5">
        <span className="text-lg font-bold">Returns Backtest</span>
        <p className="text-sm text-muted-foreground mt-1">
          <BacktestSubtitle
            hasLumpsum={hasLumpsum}
            hasSip={hasSip}
            lumpsum={proposal.total_lumpsum_amount}
            sip={proposal.total_sip_amount}
            stepUp={formatProposalStepUp(proposal)}
            periodLabel={getBacktestPeriodLabel(
              data.actual_start_date,
              data.actual_end_date,
              period
            )}
          />
        </p>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <ChartContainer config={chartConfig} className={"h-100 w-full px-5"}>
          <ComposedChart
            data={chartPoints}
            margin={{ top: 8, right: 16, bottom: 0, left: 16 }}
          >
            <defs>
              <linearGradient
                id={`backtestReportFill-${uid}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={PORTFOLIO_STROKE}
                  stopOpacity={0.4}
                />
                <stop
                  offset="95%"
                  stopColor={PORTFOLIO_STROKE}
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>
            <YAxis hide domain={["auto", "auto"]} />
            <XAxis
              dataKey="date"
              domain={["dataMin", "dataMax"]}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={formatXAxisTick}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            {!embed && (
              <ChartTooltip
                cursor={false}
                content={
                  <BacktestReportTooltip benchmarkLabel={benchmarkLabel} />
                }
              />
            )}
            <Line
              type="monotone"
              dataKey="benchmark"
              stroke={BENCHMARK_STROKE}
              strokeWidth={1.5}
              strokeDasharray="3 3"
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="portfolio"
              stroke={PORTFOLIO_STROKE}
              strokeWidth={2}
              fill={`url(#backtestReportFill-${uid})`}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          </ComposedChart>
        </ChartContainer>
        <Table>
          <TableHeader>
            <TableRow className="pointer-events-none">
              <TableHead className="w-1/3" />
              <TableHead className="text-left">
                <div className="flex items-center gap-2">
                  <span className="inline-block size-3 rounded-sm bg-chart-3" />
                  <span className="text-sm font-medium">Portfolio average</span>
                </div>
              </TableHead>
              <TableHead className="text-left">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-4 border-t-2 border-dashed border-muted-foreground" />
                  <span className="text-sm font-medium">{benchmarkLabel}</span>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="pointer-events-none">
              <TableCell className="text-muted-foreground">
                Total invested
              </TableCell>
              <TableCell className="text-left">
                {formatAsCrore(data.proposal_metrics.total_invested)}
              </TableCell>
              <TableCell className="text-left">
                {formatAsCrore(data.benchmark_metrics.total_invested)}
              </TableCell>
            </TableRow>
            <TableRow className="pointer-events-none">
              <TableCell className="text-muted-foreground">
                Final value
              </TableCell>
              <TableCell className="text-left">
                {formatAsCrore(data.proposal_metrics.final_value)}
              </TableCell>
              <TableCell className="text-left">
                {formatAsCrore(data.benchmark_metrics.final_value)}
              </TableCell>
            </TableRow>
            <TableRow className="pointer-events-none">
              <TableCell className="text-muted-foreground">XIRR</TableCell>
              <TableCell
                className={cn(
                  "text-left",
                  data.proposal_metrics.xirr > 0
                    ? "text-constructive"
                    : "text-destructive",
                  data.proposal_metrics.xirr === 0 && "text-muted-foreground"
                )}
              >
                {data.proposal_metrics.xirr > 0 ? "+" : ""}
                {formatAsPercentage(data.proposal_metrics.xirr, "-", 1)}
              </TableCell>
              <TableCell
                className={cn(
                  "text-left",
                  data.benchmark_metrics.xirr > 0
                    ? "text-constructive"
                    : "text-destructive",
                  data.benchmark_metrics.xirr === 0 && "text-muted-foreground"
                )}
              >
                {data.benchmark_metrics.xirr > 0 ? "+" : ""}
                {formatAsPercentage(data.benchmark_metrics.xirr, "-", 1)}
              </TableCell>
            </TableRow>
            <TableRow className="pointer-events-none">
              <TableCell className="text-muted-foreground">
                Standard deviation
              </TableCell>
              <TableCell className="text-left">
                {data.proposal_metrics.standard_deviation.toFixed(2)}
              </TableCell>
              <TableCell className="text-left">
                {data.benchmark_metrics.standard_deviation.toFixed(2)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground">
                Max drawdown
              </TableCell>
              <TableCell className="text-left">
                {formatAsPercentage(data.proposal_metrics.max_drawdown, "-", 2)}
              </TableCell>
              <TableCell className="text-left">
                {formatAsPercentage(
                  data.benchmark_metrics.max_drawdown,
                  "-",
                  2
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </>
  );
}

const PERIOD_LABELS: Record<string, string> = {
  "1Y": "1 year",
  "3Y": "3 years",
  "5Y": "5 years",
  "10Y": "10 years",
};

// Derives the period from the dates the backtest actually covers, which can be
// shorter than the requested period when the funds don't have enough history.
function getBacktestPeriodLabel(
  startDate: string,
  endDate: string,
  period: string
) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (!isValid(start) || !isValid(end) || end <= start) {
    return PERIOD_LABELS[period] ?? period;
  }

  // Round to the nearest month so a window that is a few days short (e.g. the
  // end date falling on the last trading day) still reads as "5 years".
  let months = differenceInMonths(end, start);
  if (differenceInDays(end, addMonths(start, months)) >= 15) months += 1;

  if (months === 0) {
    const days = differenceInDays(end, start);
    return `${days} day${days !== 1 ? "s" : ""}`;
  }
  return formatDuration(months).trim();
}

function BacktestSubtitle({
  hasLumpsum,
  hasSip,
  lumpsum,
  sip,
  stepUp,
  periodLabel,
}: {
  hasLumpsum: boolean;
  hasSip: boolean;
  lumpsum: string;
  sip: string;
  stepUp: string | null;
  periodLabel: string;
}) {
  const lumpsumFormatted = convertToCurrency(Number(lumpsum), 0);
  const sipFormatted = (
    <>
      <span className="font-semibold">{convertToCurrency(Number(sip), 0)}</span>
      {stepUp && (
        <>
          {" "}
          (
          <span className="inline-flex items-center gap-0.5 whitespace-nowrap align-middle text-xs text-muted-foreground">
            <ArrowUpWideNarrowIcon className="size-4 -mt-1" />
            {stepUp}
          </span>
          )
        </>
      )}
    </>
  );

  if (hasLumpsum && hasSip) {
    return (
      <>
        One-time investment of{" "}
        <span className="font-semibold">{lumpsumFormatted}</span> and monthly
        SIP of {sipFormatted} over{" "}
        <span className="font-semibold">{periodLabel}</span> in the proposed
        portfolio relative to benchmark.
      </>
    );
  }
  if (hasLumpsum) {
    return (
      <>
        One-time investment of{" "}
        <span className="font-semibold">{lumpsumFormatted}</span> over{" "}
        <span className="font-semibold">{periodLabel}</span> in the proposed
        portfolio relative to benchmark.
      </>
    );
  }
  return (
    <>
      Monthly SIP of {sipFormatted} over{" "}
      <span className="font-semibold">{periodLabel}</span> in the proposed
      portfolio relative to benchmark.
    </>
  );
}

function BacktestReportTooltip({
  active,
  payload,
  benchmarkLabel,
}: TooltipProps<number, string> & { benchmarkLabel: string }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as ChartPoint | undefined;
  if (!point) return null;

  const parsed = Date.parse(point.date);
  const formattedDate = Number.isNaN(parsed)
    ? point.date
    : format(parsed, "MMM dd, yyyy");

  return (
    <div className="border-border/50 bg-background min-w-56 rounded-md border p-3 text-xs shadow">
      <div className="font-semibold text-foreground">{formattedDate}</div>
      <div className="my-2 h-px bg-border" />
      <div className="flex flex-col gap-2">
        <TooltipRow
          color={PORTFOLIO_STROKE}
          name="Portfolio average"
          value={point.portfolio}
          invested={point.portfolio_invested}
        />
        <TooltipRow
          color={BENCHMARK_STROKE}
          name={benchmarkLabel}
          value={point.benchmark}
          invested={point.benchmark_invested}
        />
      </div>
    </div>
  );
}

function TooltipRow({
  color,
  name,
  value,
}: {
  color: string;
  name: string;
  value: number | undefined;
  invested?: number | undefined;
}) {
  if (typeof value !== "number") return null;
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
        style={{ backgroundColor: color }}
      />
      <span className="flex-1 text-foreground">{name}</span>
      <span className="font-medium tabular-nums">
        {convertToCurrency(value, 0)}
      </span>
    </div>
  );
}
